import {
  Alert,
  AlertIcon,
  Heading,
  HStack,
  Select,
  SimpleGrid,
  Skeleton,
} from '@chakra-ui/react';
import { useState } from 'react';

import type { FullProfileInfoData } from '../../CustomDash/components/Chart/FullProfileInfo/types';
import { useAdjacentRoleSalaries } from '../hooks/useAdjacentRoleSalaries';
import { useAdjacentRoleSkills } from '../hooks/useAdjacentRoleSkills';
import { useAdjacentRoles } from '../hooks/useAdjacentRoles';
import type { ResolvedSkill } from '../hooks/useMapUserSkills';
import type { ResolvedProfileIds } from '../hooks/useResolveProfileIds';
import { useRoleInflows } from '../hooks/useRoleInflows';
import { useRoleSalaryTrends } from '../hooks/useRoleSalaryTrends';
import type { UpliftMode } from '../IndividualPotentialPage';
import { SectionCard } from '../SectionCard';
import { AdjacentRolesPanel } from './AdjacentRolesPanel';
import { RoleTransitionInsightsPanel } from './RoleTransitionInsightsPanel';

interface CareerTrajectorySectionProps {
  profileData: FullProfileInfoData;
  resolvedIds: ResolvedProfileIds;
  resolvedSkills: ResolvedSkill[];
  isResolving: boolean;
  isSkillsLoading: boolean;
  userSalary: number;
  upliftMode: UpliftMode;
  userPercentile: number | null;
}

export const CareerTrajectorySection = ({
  resolvedIds,
  resolvedSkills,
  isResolving,
  userSalary,
  upliftMode,
  userPercentile,
}: CareerTrajectorySectionProps) => {
  const [selectedGeoIndex, setSelectedGeoIndex] = useState(0);
  const selectedGeo = resolvedIds.geoLevels[selectedGeoIndex] ?? null;

  // Shared selected role state — lifted so both panels can see it
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  // ── Data hooks ──

  const {
    adjacentRoles,
    queryRoleLevel,
    isLoading: isRolesLoading,
    error: rolesError,
  } = useAdjacentRoles(resolvedIds, selectedGeo);

  const {
    enrichedRoles,
    isLoading: isSalaryLoading,
    progress: salaryProgress,
  } = useAdjacentRoleSalaries(
    adjacentRoles,
    resolvedIds,
    selectedGeo,
    userSalary,
    queryRoleLevel?.type,
    upliftMode,
    userPercentile
  );

  const {
    skillsByRole,
    isLoading: isSkillEnrichLoading,
    progress: skillProgress,
  } = useAdjacentRoleSkills(
    adjacentRoles,
    resolvedSkills,
    queryRoleLevel?.type
  );

  const { inflowsByRole, isLoading: isInflowsLoading } = useRoleInflows(
    adjacentRoles,
    queryRoleLevel,
    selectedGeo,
    queryRoleLevel?.type
  );

  const { trends: salaryTrends, isLoading: isTrendsLoading } =
    useRoleSalaryTrends(
      adjacentRoles,
      resolvedIds,
      selectedGeo,
      queryRoleLevel?.type,
      selectedRoleId
    );

  // Find the selected enriched role for the right panel
  const selectedRole =
    selectedRoleId !== null
      ? (enrichedRoles.find((r) => r.id === selectedRoleId) ?? null)
      : null;

  // ── Render ──

  if (isResolving) {
    return (
      <SectionCard heading="Career Trajectory" spacing={4}>
        <Skeleton height="300px" borderRadius="md" />
      </SectionCard>
    );
  }

  if (!resolvedIds.roleId) {
    return (
      <SectionCard heading="Career Trajectory" spacing={4}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Unable to determine role for career trajectory analysis
        </Alert>
      </SectionCard>
    );
  }

  return (
    <SectionCard>
      <HStack justify="space-between" align="center">
        <Heading size="sm" color="gray.800">
          Career Trajectory
        </Heading>

        {resolvedIds.geoLevels.length > 1 && (
          <Select
            size="sm"
            w="200px"
            value={selectedGeoIndex}
            onChange={(e) => setSelectedGeoIndex(Number(e.target.value))}
          >
            {resolvedIds.geoLevels.map((geo, i) => (
              <option key={`${geo.type}-${geo.id}`} value={i}>
                {geo.name} ({geo.type})
              </option>
            ))}
          </Select>
        )}
      </HStack>

      {rolesError && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {rolesError}
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <AdjacentRolesPanel
          roles={enrichedRoles}
          currentRoleName={queryRoleLevel?.name ?? resolvedIds.roleName}
          isLoading={isRolesLoading}
          isSalaryLoading={isSalaryLoading}
          salaryProgress={salaryProgress}
          skillsByRole={skillsByRole}
          isSkillsLoading={isSkillEnrichLoading}
          skillProgress={skillProgress}
          upliftMode={upliftMode}
          selectedRoleId={selectedRoleId}
          onRoleSelect={setSelectedRoleId}
        />

        <RoleTransitionInsightsPanel
          selectedRole={selectedRole}
          inflowData={
            selectedRoleId !== null
              ? inflowsByRole.get(selectedRoleId)
              : undefined
          }
          isInflowsLoading={isInflowsLoading}
          salaryTrends={salaryTrends}
          isTrendsLoading={isTrendsLoading}
          currentRoleName={queryRoleLevel?.name ?? resolvedIds.roleName}
        />
      </SimpleGrid>
    </SectionCard>
  );
};
